/**
 * Marketing Content Agent
 * Generates marketing copy, social media content, newsletters
 */

interface ContentRequest {
  topic: string;
  type: 'promotional' | 'educational' | 'seasonal' | 'engagement';
  tone: 'casual' | 'professional' | 'friendly' | 'inspirational';
  audience: 'customers' | 'farmers' | 'general';
  productName?: string;
  farmName?: string;
}

interface MarketingContent {
  headline: string;
  body: string;
  callToAction: string;
  hashtags?: string[];
  emojis?: string[];
}

interface SocialMediaPost {
  platform: 'facebook' | 'instagram' | 'twitter' | 'tiktok';
  content: string;
  hashtags: string[];
  characterCount: number;
  mediaSuggestion?: string;
}

interface NewsletterContent {
  subject: string;
  previewText: string;
  sections: {
    title: string;
    content: string;
    type: 'featured' | 'educational' | 'promotional' | 'community';
  }[];
  callToAction: string;
}

class MarketingContentAgent {
  /**
   * Generate marketing copy
   */
  generateMarketingCopy(request: ContentRequest): MarketingContent {
    let headline = '';
    let body = '';
    let callToAction = '';
    let hashtags: string[] = [];

    // Generate based on type and topic
    if (request.type === 'promotional' && request.productName) {
      headline = this.generatePromotionalHeadline(request.productName);
      body = this.generatePromotionalBody(
        request.productName,
        request.tone,
        request.farmName
      );
      callToAction = 'Shop now and taste the difference!';
      hashtags = [
        '#LocalFresh',
        '#FarmToTable',
        `#${request.productName.replace(/\s+/g, '')}`,
      ];
    } else if (request.type === 'seasonal') {
      headline = this.generateSeasonalHeadline(request.topic);
      body = this.generateSeasonalBody(request.topic, request.tone);
      callToAction = 'Visit your local farm today!';
      hashtags = [
        '#SeasonalProduce',
        '#LocalFarms',
        '#SupportLocal',
        '#FreshSeason',
      ];
    } else if (request.type === 'educational') {
      headline = this.generateEducationalHeadline(request.topic);
      body = this.generateEducationalBody(request.topic, request.productName);
      callToAction = 'Learn more about our farming practices!';
      hashtags = [
        '#FarmingKnowledge',
        '#Sustainable',
        '#LocalAgriculture',
      ];
    } else if (request.type === 'engagement') {
      headline = this.generateEngagementHeadline();
      body = this.generateEngagementBody(request.tone);
      callToAction = 'Tell us your favorite farm memory!';
      hashtags = ['#FarmCommunity', '#LocalLove', '#CommunityFirst'];
    }

    return {
      headline,
      body,
      callToAction,
      hashtags,
    };
  }

  /**
   * Generate social media posts
   */
  generateSocialMediaPosts(
    request: ContentRequest,
    platforms: ('facebook' | 'instagram' | 'twitter' | 'tiktok')[] = [
      'instagram',
      'facebook',
      'twitter',
    ]
  ): SocialMediaPost[] {
    const baseContent = this.generateMarketingCopy(request);
    const posts: SocialMediaPost[] = [];

    platforms.forEach((platform) => {
      const post = this.adaptContentForPlatform(
        platform,
        baseContent,
        request
      );
      posts.push(post);
    });

    return posts;
  }

  /**
   * Generate newsletter content
   */
  generateNewsletterContent(
    farmName: string,
    season: string,
    featuredProducts: string[]
  ): NewsletterContent {
    const sections = [
      {
        title: `This ${season} at ${farmName}`,
        content: this.generateSeasonalFarmUpdate(farmName, season, featuredProducts),
        type: 'featured' as const,
      },
      {
        title: 'Produce Tips',
        content: this.generateProduceTips(featuredProducts),
        type: 'educational' as const,
      },
      {
        title: 'Seasonal Special',
        content: this.generateSeasonalPromotion(season),
        type: 'promotional' as const,
      },
      {
        title: 'Community Corner',
        content: this.generateCommunityContent(),
        type: 'community' as const,
      },
    ];

    return {
      subject: `Fresh from ${farmName} - ${season} Special`,
      previewText: `Discover this season's best produce and exclusive offers!`,
      sections,
      callToAction: 'Visit us this week!',
    };
  }

  /**
   * Generate promotional headline
   */
  private generatePromotionalHeadline(productName: string): string {
    const headlines = [
      `Fresh ${productName} - Pick Your Favorites!`,
      `🥬 Taste the Freshness of Local ${productName}`,
      `Limited Time: Premium ${productName} at Farm Prices`,
      `Just Picked: ${productName} from Local Farms`,
      `Your Neighborhood's Freshest ${productName} Awaits`,
    ];
    return headlines[Math.floor(Math.random() * headlines.length)];
  }

  /**
   * Generate promotional body
   */
  private generatePromotionalBody(
    productName: string,
    tone: string,
    farmName?: string
  ): string {
    const farmPrefix = farmName ? `From ${farmName}, ` : '';

    const bodies = [
      `${farmPrefix}nothing beats the taste of fresh ${productName} picked at the peak of ripeness. Our local farmers bring you the finest quality, straight from farm to table. Every bite is packed with nutrition and flavor that you simply can't find in stores.`,
      `Tired of grocery store produce? ${farmPrefix}experience the difference that true freshness makes. Our ${productName} is picked to order, ensuring maximum flavor and nutrition for your family.`,
      `Support local farmers and enjoy superior taste with our ${productName}. ${farmPrefix}we're committed to quality and sustainability, offering you produce grown with care and harvested at its peak.`,
    ];

    return bodies[Math.floor(Math.random() * bodies.length)];
  }

  /**
   * Generate seasonal headline
   */
  private generateSeasonalHeadline(season: string): string {
    const headlines: { [key: string]: string[] } = {
      spring: [
        "Spring Harvest: Fresh Beginnings Await",
        "Spring Greens Are Here!",
        "Welcome Spring with Farm-Fresh Produce",
      ],
      summer: [
        "Summer Bounty: Peak Season Is Here",
        "Beat the Heat with Fresh, Cool Produce",
        "Summer Harvest: Nature's Best Season",
      ],
      fall: [
        "Fall Favorites Are Back",
        "Autumn Harvest: Warmth & Flavor",
        "Cozy Season Starts with Fresh Produce",
      ],
      winter: [
        "Winter Citrus Season: Bright & Delicious",
        "Cold Months, Warm Fresh Produce",
        "Winter Greens: Healthy & Hearty",
      ],
    };

    const seasonHeadlines = headlines[season.toLowerCase()] || headlines.spring;
    return seasonHeadlines[Math.floor(Math.random() * seasonHeadlines.length)];
  }

  /**
   * Generate seasonal body
   */
  private generateSeasonalBody(season: string, tone: string): string {
    const seasonalInfo: { [key: string]: string } = {
      spring: 'Spring is the season of renewal, and our farms are bursting with fresh, tender greens and vibrant vegetables. From leafy salads to fresh herbs, this is the perfect time to refresh your meals with the season\'s best.',
      summer: 'Summer brings peak flavor! Our tomatoes have never been sweeter, berries more juicy, or peppers more colorful. Enjoy the abundance while it lasts - summer season produce is at its absolute best right now.',
      fall: 'As temperatures cool, we transition to hearty fall favorites. Root vegetables, squash, and hardy greens dominate this season, perfect for comforting, nourishing meals as we move into cooler months.',
      winter: 'Winter is citrus season! Bright, juicy oranges, grapefruits, and lemons bring vitamin C and cheerful flavor during the colder months. Plus, leafy greens thrive in our mild winter climate.',
    };

    return seasonalInfo[season.toLowerCase()] || seasonalInfo.spring;
  }

  /**
   * Generate educational headline
   */
  private generateEducationalHeadline(topic: string): string {
    return `Learn: Everything About ${topic}`;
  }

  /**
   * Generate educational body
   */
  private generateEducationalBody(topic: string, productName?: string): string {
    const product = productName || topic;
    return `Did you know that ${product} is packed with essential nutrients? Learn about the health benefits, the best ways to store and prepare ${product}, and how local farmers grow it sustainably. Understanding where your food comes from and how to use it optimally is part of supporting our local farming community.`;
  }

  /**
   * Generate engagement headline
   */
  private generateEngagementHeadline(): string {
    const headlines = [
      'Your Stories Matter',
      'Community Spotlight',
      'Join the Conversation',
      'What\'s Your Farm Love Story?',
      'Share Your Local Love',
    ];
    return headlines[Math.floor(Math.random() * headlines.length)];
  }

  /**
   * Generate engagement body
   */
  private generateEngagementBody(tone: string): string {
    return 'We love hearing from our community! Whether it\'s your favorite farm visit, a delicious recipe using local produce, or a memory from the farmers market - we want to hear it! Tag us and join the conversation about supporting local agriculture.';
  }

  /**
   * Adapt content for platform
   */
  private adaptContentForPlatform(
    platform: 'facebook' | 'instagram' | 'twitter' | 'tiktok',
    baseContent: MarketingContent,
    request: ContentRequest
  ): SocialMediaPost {
    let content = '';
    let mediaSuggestion = '';

    switch (platform) {
      case 'instagram':
        content = `${baseContent.headline}\n\n${baseContent.body.substring(0, 250)}...\n\n${baseContent.callToAction}\n\n${baseContent.hashtags?.slice(0, 15).join(' ') || ''}`;
        mediaSuggestion = 'High-quality farm/product photo (1080x1080px)';
        break;

      case 'facebook':
        content = `${baseContent.headline}\n\n${baseContent.body}\n\n${baseContent.callToAction}\n\n${baseContent.hashtags?.slice(0, 5).join(' ') || ''}`;
        mediaSuggestion = 'Farm lifestyle photo or product carousel';
        break;

      case 'twitter':
        content = `${baseContent.headline} ${baseContent.callToAction} ${baseContent.hashtags?.slice(0, 3).join(' ') || ''}`;
        mediaSuggestion = 'Relevant product/farm image';
        break;

      case 'tiktok':
        content = `${baseContent.headline}\n\n${baseContent.callToAction}\n\n${baseContent.hashtags?.slice(0, 10).join(' ') || '#FarmLife'}`;
        mediaSuggestion = 'Short, engaging farm/harvest video (15-30 seconds)';
        break;
    }

    return {
      platform,
      content,
      hashtags: baseContent.hashtags || [],
      characterCount: content.length,
      mediaSuggestion,
    };
  }

  /**
   * Generate seasonal farm update
   */
  private generateSeasonalFarmUpdate(
    farmName: string,
    season: string,
    products: string[]
  ): string {
    const productList = products.join(', ');
    return `We're thrilled to share what's growing at ${farmName} this ${season}! From ${productList}, our fields are bursting with fresh produce. This season, we're focusing on sustainable practices while bringing you the best flavors nature has to offer.`;
  }

  /**
   * Generate produce tips
   */
  private generateProduceTips(products: string[]): string {
    const tips: { [key: string]: string } = {
      tomatoes: 'Store tomatoes at room temperature to maintain flavor. Keep them away from direct sunlight.',
      peppers: 'Peppers can be stored in the refrigerator for up to 2 weeks. They\'re versatile for raw eating or cooking.',
      lettuce: 'Keep lettuce in the crisper drawer, unwashed, in a sealed container. It stays fresh for up to a week.',
      berries: 'Berries are best consumed within a few days. Store them in the refrigerator in a single layer.',
      herbs: 'Fresh herbs stay fresh longer when stored upright in a glass of water, like flowers.',
    };

    let tipContent = 'Produce Storage & Usage Tips:\n\n';
    products.forEach((product) => {
      const productKey = product.toLowerCase();
      tipContent +=
        tips[productKey] || `Fresh ${product} is best enjoyed within days of purchase for optimal flavor.\n`;
    });

    return tipContent;
  }

  /**
   * Generate seasonal promotion
   */
  private generateSeasonalPromotion(season: string): string {
    const promotions: { [key: string]: string } = {
      spring: 'Spring Special: Buy 3 items from our spring collection and get 10% off! Perfect for spring meal prep.',
      summer: 'Summer Savings: Get fresh berry picks weekly and save 15% on bulk orders. Beat the heat with fresh juice!',
      fall: 'Fall Festival Special: Purchase any 5 fall vegetables and receive a free dozen eggs. Limited time!',
      winter: 'Winter Warmth Deal: Buy our citrus collection and receive a free jar of homemade jam. Stock up!',
    };

    return (
      promotions[season.toLowerCase()] ||
      'Seasonal Special: Contact us for this week\'s exclusive deals!'
    );
  }

  /**
   * Generate community content
   */
  private generateCommunityContent(): string {
    const contents = [
      'Thanks to everyone who visited us at the farmers market last week! Your support helps our farms thrive.',
      'Spotlight on one of our farmers: Meet the people behind your fresh produce and learn their farming stories.',
      'Community Recipe: A reader shared this amazing recipe using our tomatoes - try it and tag us with your photos!',
      'Farm Visit Highlights: Families enjoyed picking their own strawberries. See the smiles and fresh fun!',
    ];

    return contents[Math.floor(Math.random() * contents.length)];
  }
}

export default new MarketingContentAgent();
